import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';

/**
 * Mock Authentication Service
 * Replaces MsalService when disableAuth is enabled in environment
 * Provides the same interface as MsalService but returns mock data
 */
@Injectable({
  providedIn: 'root'
})
export class MockAuthService {

  private mockAccount = {
    homeAccountId: 'mock-home-account-id',
    environment: 'mock-environment',
    tenantId: 'mock-tenant-id',
    username: 'mockuser@test.com',
    localAccountId: 'mock-local-account-id',
    name: 'Mock User',
    idTokenClaims: {
      aud: 'mock-audience',
      iss: 'mock-issuer',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600,
      name: 'Mock User',
      preferred_username: 'mockuser@test.com',
      oid: 'mock-object-id',
      sub: 'mock-subject',
      tid: 'mock-tenant-id',
      ver: '2.0'
    }
  };

  /**
   * Mock MSAL instance
   */
  instance = {
    getAllAccounts: () => [this.mockAccount],
    getActiveAccount: () => this.mockAccount,
    setActiveAccount: (account: any) => {
      console.log('[Mock Auth] setActiveAccount called with:', account);
    },
    enableAccountStorageEvents: () => {
      console.log('[Mock Auth] enableAccountStorageEvents called');
    },
    handleRedirectPromise: () => Promise.resolve(null),
    acquireTokenSilent: (request: any) => {
      console.log('[Mock Auth] acquireTokenSilent called with:', request);
      return Promise.resolve({
        accessToken: 'mock-access-token',
        idToken: 'mock-id-token',
        account: this.mockAccount,
        scopes: request.scopes || [],
        expiresOn: new Date(Date.now() + 3600000),
        tokenType: 'Bearer',
        uniqueId: 'mock-unique-id',
        tenantId: 'mock-tenant-id'
      });
    },
    acquireTokenPopup: (request: any) => {
      console.log('[Mock Auth] acquireTokenPopup called with:', request);
      return Promise.resolve({
        accessToken: 'mock-access-token',
        idToken: 'mock-id-token',
        account: this.mockAccount,
        scopes: request.scopes || [],
        expiresOn: new Date(Date.now() + 3600000),
        tokenType: 'Bearer',
        uniqueId: 'mock-unique-id',
        tenantId: 'mock-tenant-id'
      });
    },
    loginPopup: (request?: any) => {
      console.log('[Mock Auth] loginPopup called with:', request);
      return Promise.resolve({
        accessToken: 'mock-access-token',
        idToken: 'mock-id-token',
        account: this.mockAccount,
        scopes: request?.scopes || [],
        expiresOn: new Date(Date.now() + 3600000),
        tokenType: 'Bearer',
        uniqueId: 'mock-unique-id',
        tenantId: 'mock-tenant-id'
      });
    },
    loginRedirect: (request?: any) => {
      console.log('[Mock Auth] loginRedirect called with:', request);
      return Promise.resolve();
    },
    logout: (logoutRequest?: any) => {
      console.log('[Mock Auth] logout called with:', logoutRequest);
      return Promise.resolve();
    },
    logoutPopup: (logoutRequest?: any) => {
      console.log('[Mock Auth] logoutPopup called with:', logoutRequest);
      return Promise.resolve();
    },
    logoutRedirect: (logoutRequest?: any) => {
      console.log('[Mock Auth] logoutRedirect called with:', logoutRequest);
      return Promise.resolve();
    }
  };

  constructor() {
    console.log('[Mock Auth Service] Initialized - Authentication is DISABLED');
  }

  /**
   * Initialize the mock auth service
   */
  initialize(): Observable<void> {
    console.log('[Mock Auth] initialize called');
    return of(void 0);
  }

  /**
   * Handle redirect promise (mock - does nothing)
   */
  handleRedirectObservable(): Observable<any> {
    console.log('[Mock Auth] handleRedirectObservable called');
    return of(null);
  }

  /**
   * Login with popup (mock - instantly succeeds)
   */
  loginPopup(request?: any): Observable<any> {
    console.log('[Mock Auth] loginPopup called');
    return of({
      accessToken: 'mock-access-token',
      account: this.mockAccount
    });
  }

  /**
   * Login with redirect (mock - does nothing)
   */
  loginRedirect(request?: any): Observable<void> {
    console.log('[Mock Auth] loginRedirect called');
    return of(void 0);
  }

  /**
   * Logout with popup (mock - instantly succeeds)
   */
  logoutPopup(request?: any): Observable<void> {
    console.log('[Mock Auth] logoutPopup called');
    return of(void 0);
  }

  /**
   * Logout with redirect (mock - does nothing)
   */
  logoutRedirect(request?: any): Observable<void> {
    console.log('[Mock Auth] logoutRedirect called');
    return of(void 0);
  }

  /**
   * Acquire token silently (mock - returns fake token)
   */
  acquireTokenSilent(request: any): Observable<any> {
    console.log('[Mock Auth] acquireTokenSilent called');
    return of({
      accessToken: 'mock-access-token',
      account: this.mockAccount,
      scopes: request.scopes || []
    });
  }

  /**
   * Acquire token with popup (mock - returns fake token)
   */
  acquireTokenPopup(request: any): Observable<any> {
    console.log('[Mock Auth] acquireTokenPopup called');
    return of({
      accessToken: 'mock-access-token',
      account: this.mockAccount,
      scopes: request.scopes || []
    });
  }

  /**
   * Get all accounts (mock - returns single mock account)
   */
  getAllAccounts(): any[] {
    return [this.mockAccount];
  }

  /**
   * Get active account (mock - returns mock account)
   */
  getActiveAccount(): any {
    return this.mockAccount;
  }

  /**
   * Set active account (mock - does nothing)
   */
  setActiveAccount(account: any): void {
    console.log('[Mock Auth] setActiveAccount called');
  }
}
